import queryString from "../queryString/index";
/** @hidden */
class CapiPageError extends Error {
  constructor(m) {
    super(m);
    // Set the prototype explicitly because when this is transpiled to ES5, it
    // uses a class constructor pattern that won't properly extend the builtin
    // Error class. See:
    // https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    Object.setPrototypeOf(this, CapiPageError.prototype);
  }
}
/** @hidden */
export class Pager {
  /**
   * Make a new CAPI Pager
   *
   * @param path the CAPI path, with no proxy prefixes and *no query params!*
   *   (eg, "/projects")
   * @param params any query params that should be included on all requests
   *   (pagination query params should *not* be included here)
   */
  constructor(path, params, rawCapiClient) {
    this.isPageFetched = null;
    this.data = [];
    this.path = path;
    this.params = Object.assign({}, params, {
      limit: Pager.PER_PAGE,
    });
    this.totalPages = null;
    this.hasError = false;
    this.rawCapiClient = rawCapiClient;
  }
  static validateCapiResponse(jsonResponse) {
    if (!jsonResponse.data || !Array.isArray(jsonResponse.data)) {
      return Promise.reject(
        new Error("missing or invalid `data` property on CAPI response")
      );
    }
    if (typeof jsonResponse.total_count !== "number") {
      return Promise.reject(
        new Error("missing or invalid `total_count` property on CAPI response")
      );
    }
    return jsonResponse;
  }
  startPaging() {
    return new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
      return this.fetchInitialPage()
        .then(json => Pager.validateCapiResponse(json))
        .then(json => {
          this.setupFromFirstPage(json.total_count);
          this.receivePage(0, json.data);
        })
        .then(() => this.fetchSubsequentPages())
        .catch(err => this.handleError(err));
    });
  }
  // this is overridden in tests, should be considered private otherwise
  get({ url, params }) {
    return this.rawCapiClient.get({ url, params });
  }
  // this is overridden in tests, should be considered private otherwise
  post({ url, params }, body) {
    return this.rawCapiClient.post({ url, params }, body);
  }
  fetchInitialPage() {
    return this.get({ url: this.path, params: this.params });
  }
  fetchSubsequentPages() {
    if (this.resolveIfDone()) return;
    const batches = [];
    const totalBatches = Math.ceil((this.totalPages - 1) / Pager.PER_BATCH);
    let startPage = 1;
    for (let i = 0; i < totalBatches; i += 1) {
      const batch = this.makeBatch(startPage);
      batches.push(batch);
      startPage += Pager.PER_BATCH;
    }
    // We limit the number of concurrent requests to avoid overwhelming CAPI. Make an initial set
    // of requests, then as each finishes, send another request until there are no more to send.
    let next;
    const sendNext = () => {
      if (next < batches.length && !this.hasError) {
        const curr = next;
        next += 1;
        const thisBatch = batches[curr];
        this.fetchBatch(thisBatch).then(response => {
          this.handleBatchResponse(curr, response);
          sendNext();
        });
      }
    };
    const maxConcurrent = Pager.MAX_CONCURRENT_REQUESTS;
    let i = 0;
    while (i < maxConcurrent && i < batches.length && !this.hasError) {
      const thisBatch = batches[i];
      const thisBatchNumber = i;
      this.fetchBatch(thisBatch).then(response => {
        this.handleBatchResponse(thisBatchNumber, response);
        sendNext();
      });
      i += 1;
    }
    next = maxConcurrent;
  }
  fetchBatch(batch) {
    return this.post(
      {
        url: "/batch",
      },
      batch
    ).catch(err => this.handleError(err));
  }
  makeBatch(startPage) {
    const batch = [];
    let i = startPage;
    while (i < this.totalPages && batch.length < Pager.PER_BATCH) {
      const params = Object.assign({}, this.params, {
        skip: i * Pager.PER_PAGE,
      });
      batch.push({
        method: "GET",
        path: `${this.path}?${queryString.stringify(params)}`,
      });
      i += 1;
    }
    return batch;
  }
  handleBatchResponse(batchNumber, response) {
    if (this.hasError) return;
    for (let i = 0; i < response.data.length; i += 1) {
      const res = response.data[i];
      if (res.status_code > 299 || res.status_code < 200) {
        this.handleError(JSON.parse(res.body));
        return;
      }
      const body = JSON.parse(res.body);
      this.receivePage(batchNumber * Pager.PER_BATCH + 1 + i, body.data);
    }
    this.resolveIfDone();
  }
  setupFromFirstPage(totalItems) {
    this.totalPages = Math.ceil(totalItems / Pager.PER_PAGE);
    this.isPageFetched = [];
    for (let i = 0; i < this.totalPages; i += 1) this.isPageFetched[i] = false;
  }
  receivePage(pageNumber, data) {
    if (this.hasError) return;
    this.isPageFetched[pageNumber] = true;
    const offset = pageNumber * Pager.PER_PAGE;
    for (let i = offset; i < offset + data.length; i += 1) {
      this.data[i] = data[i - offset];
    }
    this.resolveIfDone();
  }
  resolveIfDone() {
    const isPageFetched = this.isPageFetched;
    if (isPageFetched.every(fetched => fetched)) {
      this.resolve(this.data);
      return true;
    }
    return false;
  }
  handleError(err) {
    this.hasError = true;
    if (err instanceof Error) {
      this.reject(err);
    } else {
      const jsError = new CapiPageError(err.message || "CAPI Pager Error");
      jsError.status = err.status;
      this.reject(jsError);
    }
  }
}
Pager.PER_PAGE = 25;
Pager.PER_BATCH = 10;
Pager.MAX_CONCURRENT_REQUESTS = 5;
/**
 * Fetch all pages of a CAPI resource
 *
 * This method makes multiple concurrent GET requests to CAPI to fetch all resources from a
 * paginated endpoint.
 *
 * @param path {string} the CAPI URL path, not including any query params (eg, "/capi/workspaces")
 * @param params {object} the query params, not including any pagination query params like `skip`
 *    or `limit`. (eg, `{ updated_after: '2017-07-01' }`)
 * @returns {Promise} resolves with an array of results from CAPI or rejects with the first error
 *    response that any request returns.
 */
export function fetchAllPages(path, params, rawCapiClient) {
  const pager = new Pager(path, params, rawCapiClient);
  return pager.startPaging();
}
